import { Box, Button, TextField, Typography, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import themeColors from "../theme";
import Logo from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { isValidEmail } from "../utils/validation";
import { isProfileComplete } from "../utils/profile";

export default function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const { teal, darkTeal, lightGray } = themeColors;

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
    const [forgotPasswordError, setForgotPasswordError] = useState("");
    const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setEmailError("");
        if (!isValidEmail(email)) {
            setEmailError("Please enter a valid email address.");
            return;
        }
        if (!password) {
            setError("Please enter your password.");
            return;
        }
        try {
            const res = await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/login`, { email, password });
            login(res.data.token, res.data.refreshToken);

            // Check if profile is complete to determine redirect
            try {
                const profileRes = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/profile`, {
                    headers: { Authorization: `Bearer ${res.data.token}` }
                });
                const profile = profileRes.data;

                if (isProfileComplete(profile)) {
                    navigate("/dashboard");
                } else {
                    navigate("/profile?new=true");
                }
            } catch {
                // If profile fetch fails (e.g., no profile exists), redirect to profile page
                navigate("/profile?new=true");
            }
        } catch (err) {
            // Check if the error is due to unverified email
            if (err.response?.data?.code === "EMAIL_NOT_VERIFIED") {
                navigate("/email-not-verified", { state: { email: err.response.data.email } });
                return;
            }
            setError("Incorrect email or password. Please try again.");
        }
    };

    const handleForgotPassword = () => {
        setForgotPasswordOpen(true);
        setForgotPasswordEmail("");
        setForgotPasswordError("");
        setForgotPasswordSuccess(false);
    };

    const handleForgotPasswordSubmit = async (e) => {
        e.preventDefault();
        setForgotPasswordError("");
        setLoading(true);

        if (!forgotPasswordEmail) {
            setForgotPasswordError("Please enter your email address");
            setLoading(false);
            return;
        }

        try {
            await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/forgot-password`, {
                email: forgotPasswordEmail
            });
            setForgotPasswordSuccess(true);
        } catch (err) {
            setForgotPasswordError(err.response?.data?.error || "Failed to send reset email. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleCloseForgotPassword = () => {
        setForgotPasswordOpen(false);
        setForgotPasswordEmail("");
        setForgotPasswordError("");
        setForgotPasswordSuccess(false);
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: lightGray,
                px: 2,
            }}
        >
            <Box sx={{ textAlign: "center", mb: 4 }}>
                <Logo/>
            </Box>

            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    width: "100%",
                    maxWidth: 470,
                    backgroundColor: "#fff",
                    p: 5,
                    borderRadius: 2,
                    boxShadow: 3,
                    textAlign: "center",
                }}
            >
                <Typography variant="h4" fontWeight="600" mb={2} sx={{ color: "#1D3557" }}>
                    Sign in
                </Typography>

                <Typography variant="body1" sx={{ color: "#5A6C7D", mb: 3 }}>
                    Sign in to manage your CME funds, credits,
                    <br />
                    and documents.
                </Typography>

                <TextField
                    fullWidth
                    margin="normal"
                    placeholder="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={!!emailError}
                    helperText={emailError}
                    sx={{ mb: 2 }}
                    InputLabelProps={{ shrink: false }}
                />

                <TextField
                    fullWidth
                    margin="normal"
                    placeholder="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    InputLabelProps={{ shrink: false }}
                />

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                        mt: 1,
                        mb: 2,
                        py: 1.5,
                        backgroundColor: teal,
                        '&:hover': { backgroundColor: darkTeal },
                        textTransform: "uppercase",
                        fontWeight: 600,
                        fontSize: "0.95rem"
                    }}
                >
                    SIGN IN
                </Button>

                <Button
                    variant="text"
                    onClick={handleForgotPassword}
                    sx={{ color: "#1D3557", textTransform: "none", fontWeight: 500, mb: 2 }}
                >
                    Forgot password?
                </Button>

                <Box sx={{ borderTop: "1px solid #E0E0E0", pt: 2 }}>
                    <Typography variant="body2" sx={{ color: "#5A6C7D" }}>
                        Don't have an account?{" "}
                        <Button
                            variant="text"
                            sx={{ color: teal, textTransform: "none", p: 0, minWidth: "auto", fontWeight: 500 }}
                            onClick={() => navigate("/register")}
                        >
                            Create one
                        </Button>
                    </Typography>
                </Box>

                <Typography variant="caption" sx={{ display: "block", color: "#5A6C7D", mt: 3 }}>
                    Secure sign-in. Your data is protected.
                </Typography>
            </Box>

            <Dialog open={forgotPasswordOpen} onClose={handleCloseForgotPassword} maxWidth="sm" fullWidth>
                <DialogTitle>Reset Password</DialogTitle>
                <DialogContent>
                    {forgotPasswordSuccess ? (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            If an account with that email exists, a password reset link has been sent. Please check your email.
                        </Alert>
                    ) : (
                        <>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Enter your email address and we'll send you a link to reset your password.
                            </Typography>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Email"
                                type="email"
                                value={forgotPasswordEmail}
                                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                                disabled={loading}
                            />
                            {forgotPasswordError && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {forgotPasswordError}
                                </Alert>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    {forgotPasswordSuccess ? (
                        <Button onClick={handleCloseForgotPassword} variant="contained" sx={{ backgroundColor: teal, '&:hover': { backgroundColor: darkTeal } }}>
                            Close
                        </Button>
                    ) : (
                        <>
                            <Button onClick={handleCloseForgotPassword} disabled={loading}>
                                Cancel
                            </Button>
                            <Button 
                                onClick={handleForgotPasswordSubmit} 
                                variant="contained" 
                                disabled={loading}
                                sx={{ backgroundColor: teal, '&:hover': { backgroundColor: darkTeal } }}
                            >
                                {loading ? "Sending..." : "Send Reset Link"}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
}
